import { createContext, useContext, useState, useRef } from "react";
import driverApi from "../utils/driverApi";

const DriverAuthContext = createContext();

function loadDriver() {
  try { return JSON.parse(localStorage.getItem("trackin_driver") || "null"); }
  catch { return null; }
}

// Fully parallel to AuthContext — a different session shape (driver, not org),
// a different storage key, and a different backend surface (/api/driver-auth,
// /api/driver/*). Never shares state with AuthContext.
export function DriverAuthProvider({ children }) {
  const [driver, setDriverState] = useState(() => loadDriver());
  const driverRef = useRef(driver);

  function setDriver(next) {
    const value = typeof next === "function" ? next(driverRef.current) : next;
    driverRef.current = value;
    setDriverState(value);
    if (value) localStorage.setItem("trackin_driver", JSON.stringify(value));
    else        localStorage.removeItem("trackin_driver");
  }

  const driverSession = driver ? { driverId: driver.id, name: driver.name } : null;

  async function driverLogin(email, password) {
    const { data } = await driverApi.post("/api/driver-auth/login", { email, password });
    localStorage.setItem("trackin_driver_token", data.token);
    setDriver(data.driver);
    return data.driver;
  }

  function driverLogout() {
    localStorage.removeItem("trackin_driver_token");
    setDriver(null);
  }

  function updateDriverSession(patch) {
    setDriver(prev => (prev ? { ...prev, ...patch } : prev));
  }

  return (
    <DriverAuthContext.Provider value={{ driver, driverSession, driverLogin, driverLogout, updateDriverSession }}>
      {children}
    </DriverAuthContext.Provider>
  );
}

export function useDriverAuth() {
  const ctx = useContext(DriverAuthContext);
  if (!ctx) throw new Error("useDriverAuth must be inside DriverAuthProvider");
  return ctx;
}
